import axios from 'axios';
import { fuzzyNormalize } from '../../utils/translit';
import { normalize, isArtistMatch, isValidArtwork } from '../../utils/stringCleaners';

/**
 * iTunes Search API Provider
 * Primary source for album art (works in Russia)
 */

const SEARCH_TIMEOUT = 20000;

/**
 * Search iTunes for track artwork
 * @param {string} queryArtist - Artist name to search
 * @param {string} queryTrack - Track name to search
 * @param {string} validateArtist - Artist name for validation
 * @returns {Promise<string|null>} - Artwork URL or null
 */
export const searchITunes = async (queryArtist, queryTrack, validateArtist) => {
    try {
        const term = `${queryArtist} ${queryTrack}`.trim();
        console.log(`[iTunes] Query: "${term}"`);

        const response = await axios.get('https://itunes.apple.com/search', {
            params: {
                term: term,
                media: 'music',
                entity: 'song',
                limit: 15,
            },
            timeout: SEARCH_TIMEOUT
        });

        const results = response.data.results || [];
        const targetArtistNorm = normalize(validateArtist);
        const targetTrackNorm = normalize(queryTrack);
        const isTargetLive = targetTrackNorm.includes('live');

        // PASS 1: EXACT MATCH
        for (const item of results) {
            if (!item.artworkUrl100 || !isValidArtwork(item.artworkUrl100)) continue;
            const itemArtistNorm = normalize(item.artistName);
            const itemTrackNorm = normalize(item.trackName);

            if (itemArtistNorm === targetArtistNorm && itemTrackNorm === targetTrackNorm) {
                console.log(`[iTunes] Exact Match: "${item.artistName}" - "${item.trackName}"`);
                return item.artworkUrl100.replace('100x100', '600x600');
            }
        }

        // PASS 2: PARTIAL/FUZZY MATCH
        for (const item of results) {
            if (!item.artworkUrl100 || !isValidArtwork(item.artworkUrl100)) continue;

            if (isArtistMatch(item.artistName, validateArtist)) {
                const itemTrackNorm = normalize(item.trackName);
                const isItemLive = itemTrackNorm.includes('live');

                if (isTargetLive === isItemLive) {
                    const fuzzyItemTrack = fuzzyNormalize(itemTrackNorm);
                    const fuzzyTargetTrack = fuzzyNormalize(targetTrackNorm);

                    if (itemTrackNorm.includes(targetTrackNorm) || targetTrackNorm.includes(itemTrackNorm) ||
                        fuzzyItemTrack.includes(fuzzyTargetTrack) || fuzzyTargetTrack.includes(fuzzyItemTrack)) {

                        console.log(`[iTunes] Match: "${item.artistName}" - "${item.trackName}"`);
                        return item.artworkUrl100.replace('100x100', '600x600');
                    }
                }
            }
        }
    } catch (e) {
        if (e.response && e.response.status === 403) {
            console.warn('[iTunes] Rate Limit/Forbidden (403) - Skipping');
        } else {
            console.warn('[iTunes] Error:', e.message || e);
        }
    }
    return null;
};

/**
 * Search iTunes for artist photo (fallback)
 * @param {string} cleanArtist - Cleaned artist name
 * @returns {Promise<string|null>} - Artwork URL or null
 */
export const searchITunesArtist = async (cleanArtist) => {
    try {
        console.log(`[iTunes] Searching artist: "${cleanArtist}"`);
        const response = await axios.get('https://itunes.apple.com/search', {
            params: {
                term: cleanArtist,
                media: 'music',
                entity: 'musicArtist',
                limit: 5
            },
            timeout: SEARCH_TIMEOUT
        });

        if (response.data && response.data.results && response.data.results.length > 0) {
            // Search for an album by this artist
            return await searchITunes(cleanArtist, '', cleanArtist);
        }
    } catch (e) {
        console.warn('[iTunes Artist] Error:', e);
    }
    return null;
};
